'use client';

import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import type { Activity } from '../services/activities.service';
import { type ReviewItem, type ReviewStatus, updateReviewStatus } from '../services/reviews.service';
import { applyReviewStatusToActivityRating } from '../utils/applyReviewStatusToActivityRating';

type UpdateReviewMutationContext = {
  snapshots: ReadonlyArray<[QueryKey, Activity | null | undefined]>;
};

export interface UpdateReviewStatusVars {
  reviewId: string;
  status: 'approved' | 'rejected';
  activityId: string;
  /** Review status before this mutation (drives aggregate delta). */
  previousStatus: ReviewStatus;
  reviewRating: number;
}

export function useUpdateReviewStatus() {
  const queryClient = useQueryClient();

  return useMutation<ReviewItem, Error, UpdateReviewStatusVars, UpdateReviewMutationContext>({
    mutationFn: ({ reviewId, status }) => updateReviewStatus(reviewId, status),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['activity', variables.activityId] });
      const snapshots = queryClient.getQueriesData<Activity | null>({
        queryKey: ['activity', variables.activityId]
      });

      queryClient.setQueriesData<Activity | null>({ queryKey: ['activity', variables.activityId] }, (old) => {
        if (!old) return old;
        return applyReviewStatusToActivityRating(old, {
          previousStatus: variables.previousStatus,
          newStatus: variables.status,
          reviewRating: variables.reviewRating
        });
      });

      return { snapshots };
    },
    onError: (_err, _variables, context) => {
      if (!context?.snapshots?.length) return;
      for (const [queryKey, data] of context.snapshots) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reviews', variables.activityId] });
      queryClient.invalidateQueries({ queryKey: ['activity', variables.activityId] });
    }
  });
}
